<script setup lang="ts">
import FinestFieldGrid from '@/components/finest-field-grid/FinestFieldGrid.vue'
import type {
  InlineEditableCommitPayload,
  InlineEditableField,
  InlineEditableModel,
  InlineEditableValue,
} from '@/components/finest-field-grid/types'
import { useSyncedModel } from '@/composables/useSyncedModel'
import { usePersonUpdate } from '@/pages/investors/profile/usePersonUpdate'
import NaturalPersonData = App.Data.User.NaturalPersonData

const props = defineProps<{
  member: NaturalPersonData
  fields: InlineEditableField[]
}>()

const COMMUNITY_OF_ACCRUED_GAINS = 'has_community_of_accrued_gains'
const MARITAL_CONTRACT = 'has_marital_contract'

const invert = (value: InlineEditableValue): boolean | null => (typeof value === 'boolean' ? !value : null)

const seedModel = (): InlineEditableModel => {
  const source = props.member as Record<string, InlineEditableValue>

  return Object.fromEntries(
    props.fields.map((field) => [
      field.key,
      field.key === COMMUNITY_OF_ACCRUED_GAINS ? invert(source[MARITAL_CONTRACT]) : (source[field.key] ?? null),
    ]),
  )
}

const toPersonPatch = (key: string, value: InlineEditableValue): InlineEditableModel =>
  key === COMMUNITY_OF_ACCRUED_GAINS ? { [MARITAL_CONTRACT]: invert(value) } : { [key]: value }

const toModelPatch = (patch: InlineEditableModel): InlineEditableModel =>
  MARITAL_CONTRACT in patch ? { [COMMUNITY_OF_ACCRUED_GAINS]: invert(patch[MARITAL_CONTRACT]) } : patch

const { model, confirm } = useSyncedModel(seedModel)

const update = usePersonUpdate<InlineEditableModel>(props.member.id ?? '', (savedPatch) =>
  confirm(toModelPatch(savedPatch)),
)

const save = ({ key, value }: InlineEditableCommitPayload) => update(toPersonPatch(key, value))
</script>

<template>
  <FinestFieldGrid v-model="model" :fields="fields" @commit="save" />
</template>
